// frontend/src/components/EditContactModal.jsx
// Project:      Vision ANPR by Castle Traffic Ltd
// Author:       Leon du Plooy
// Updated:      2026-09-15 08:02 BST
//
// Description:  Modal for editing contact details with company linkage,
//               proper scroll behavior, alert, and dashboard sync.
//
// Mobile Responsiveness (2026-09-15):
//   • Form rows collapse to a single column on phones and retain the existing
//     multi-column desktop layout from the sm breakpoint upward.
//   • Controls use touch-safe minimum heights and full-width mobile sizing.
//   • Flags wrap cleanly without horizontal overflow.
//   • Notes and the primary action remain usable on narrow screens.
//
// Tenant Security (2026-09-15):
//   • Removes the historical tenant_id = 1 fallback from form state and PUT payload.
//   • Tenant scope remains backend-owned via the authenticated user/JWT context.
//
// Operational Notes:
//   • Existing company loading, contact field set, update endpoint, dashboard refresh,
//     Escape handling, alert behaviour, and desktop field ordering are preserved.

import React, { useEffect, useState } from "react";
import ModalWrapper from "./ModalWrapper";
import axiosInstance from "../api/axiosInstance";
import { useDashboardRefresh } from "../context/DashboardRefreshContext";

const EditContactModal = ({ contact, onClose }) => {
  const [form, setForm] = useState(null);
  const [companies, setCompanies] = useState([]);
  const { triggerRefresh } = useDashboardRefresh();

  // Load active companies for the Company select
  useEffect(() => {
    let cancelled = false;
    const loadCompanies = async () => {
      try {
        const res = await axiosInstance.get("customers?limit=1000");
        if (!cancelled) {
          const active = (res.data || []).filter((c) => c.status === "Active");
          setCompanies(active);
        }
      } catch (e) {
        if (!cancelled) setCompanies([]);
        console.warn("❌ Failed to load companies:", e?.response?.data || e?.message || e);
      }
    };
    loadCompanies();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (contact && typeof contact === "object") {
      // Ensure newly added fields exist on the form object
      setForm({
        // original fields
        first_name: "",
        last_name: "",
        email: "",
        phone_number: "",
        status: "Active",
        role: "",
        access_level: "",
        notes: "",
        portal_access: true,
        is_primary: false,
        customer_id: "", // <-- expose/link company in edit

        // NEW fields (Mon/Tue additions)
        title: "",
        department: "",
        job_title: "",
        secondary_email: "",
        mobile_number: "",
        direct_line: "",
        extension: "",
        whatsapp: "",
        preferred_contact_method: "", // Email | Phone | WhatsApp
        is_billing_contact: false,
        is_technical_contact: false,

        // merge current contact last (so existing values override defaults)
        ...contact,
        // normalize customer_id to "" (select placeholder) when missing
        customer_id:
          typeof contact.customer_id === "number" && !Number.isNaN(contact.customer_id)
            ? contact.customer_id
            : "",
      });
    }

    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [contact, onClose]);

  const handleChange = (field, value) => {
    // Coerce numeric for customer_id; booleans handled at checkbox onChange
    if (field === "customer_id") {
      setForm((prev) => ({ ...prev, [field]: value === "" ? "" : Number(value) }));
      return;
    }
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdate = async () => {
    try {
      if (!form?.id) throw new Error("Missing contact ID");

      // Build payload — include existing fields and coerce booleans.
      // Tenant scope is intentionally NOT supplied by the frontend.
      // Omit customer_id if left as placeholder "" (no change).
      const payload = {
        first_name: form.first_name || "",
        last_name: form.last_name || "",
        email: form.email || "",
        phone_number: form.phone_number || "",
        status: form.status || "Active",
        role: form.role || "",
        access_level: form.access_level || "",
        notes: form.notes || "",
        portal_access: !!form.portal_access,
        is_primary: !!form.is_primary,

        // NEW fields
        title: form.title || "",
        department: form.department || "",
        job_title: form.job_title || "",
        secondary_email: form.secondary_email || "",
        mobile_number: form.mobile_number || "",
        direct_line: form.direct_line || "",
        extension: form.extension || "",
        whatsapp: form.whatsapp || "",
        preferred_contact_method: form.preferred_contact_method || "",
        is_billing_contact: !!form.is_billing_contact,
        is_technical_contact: !!form.is_technical_contact,
      };

      // Only send customer_id when explicitly set/changed
      if (form.customer_id !== "") {
        payload.customer_id = Number(form.customer_id);
      }

      // IMPORTANT: no leading slash so axiosInstance(baseURL=/api) keeps the /api segment
      // ✅ Recommended tweak: include auth header explicitly
      await axiosInstance.put(`contacts/${form.id}`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("anpr_token")}` },
      });

      alert("Contact updated successfully");
      triggerRefresh();
      onClose();
    } catch (err) {
      console.error("Contact update failed:", err?.response?.data || err?.message || err);
      alert("Failed to update contact.");
    }
  };

  if (!form) return null;

  return (
    <ModalWrapper onClose={onClose}>
      <h3 className="mb-4 break-words text-xl font-semibold">
        Edit Contact: {form.first_name || ""} {form.last_name || ""}
      </h3>

      <div className="flex flex-col gap-3">
        {/* Identity */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[10rem_minmax(0,1fr)_minmax(0,1fr)]">
          <select
            value={form.title || ""}
            onChange={(e) => handleChange("title", e.target.value)}
            className="min-h-[44px] w-full rounded border bg-white p-2"
          >
            <option value="">Title</option>
            <option>Mr</option>
            <option>Ms</option>
            <option>Mrs</option>
            <option>Dr</option>
            <option>Prof</option>
          </select>

          <input
            type="text"
            value={form.first_name || ""}
            onChange={(e) => handleChange("first_name", e.target.value)}
            placeholder="First Name"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
          <input
            type="text"
            value={form.last_name || ""}
            onChange={(e) => handleChange("last_name", e.target.value)}
            placeholder="Last Name"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
        </div>

        {/* Company */}
        <div className="grid grid-cols-1 gap-3">
          <select
            value={form.customer_id === "" ? "" : Number(form.customer_id)}
            onChange={(e) => handleChange("customer_id", e.target.value)}
            className="min-h-[44px] w-full min-w-0 rounded border bg-white p-2"
          >
            <option value="">Select Company</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* Employment */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            type="text"
            value={form.department || ""}
            onChange={(e) => handleChange("department", e.target.value)}
            placeholder="Department"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
          <input
            type="text"
            value={form.job_title || ""}
            onChange={(e) => handleChange("job_title", e.target.value)}
            placeholder="Job Title"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
        </div>

        {/* Emails */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            type="email"
            value={form.email || ""}
            onChange={(e) => handleChange("email", e.target.value)}
            placeholder="Email"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
          <input
            type="email"
            value={form.secondary_email || ""}
            onChange={(e) => handleChange("secondary_email", e.target.value)}
            placeholder="Secondary Email"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
        </div>

        {/* Phones */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            type="tel"
            value={form.phone_number || ""}
            onChange={(e) => handleChange("phone_number", e.target.value)}
            placeholder="Main Phone"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
          <input
            type="tel"
            value={form.mobile_number || ""}
            onChange={(e) => handleChange("mobile_number", e.target.value)}
            placeholder="Mobile"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            type="tel"
            value={form.direct_line || ""}
            onChange={(e) => handleChange("direct_line", e.target.value)}
            placeholder="Direct Line"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
          <div className="grid w-full grid-cols-[7rem_minmax(0,1fr)] gap-3">
            <input
              type="text"
              value={form.extension || ""}
              onChange={(e) => handleChange("extension", e.target.value)}
              placeholder="Ext."
              className="min-h-[44px] w-full min-w-0 rounded border p-2"
            />
            <input
              type="text"
              value={form.whatsapp || ""}
              onChange={(e) => handleChange("whatsapp", e.target.value)}
              placeholder="WhatsApp"
              className="min-h-[44px] w-full min-w-0 rounded border p-2"
            />
          </div>
        </div>

        {/* Preferences */}
        <div className="grid grid-cols-1 gap-3">
          <select
            value={form.preferred_contact_method || ""}
            onChange={(e) => handleChange("preferred_contact_method", e.target.value)}
            className="min-h-[44px] w-full min-w-0 rounded border bg-white p-2"
          >
            <option value="">Preferred Contact Method</option>
            <option value="Email">Email</option>
            <option value="Phone">Phone</option>
            <option value="WhatsApp">WhatsApp</option>
          </select>
        </div>

        {/* Status / Role / Access */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <select
            value={form.status || "Active"}
            onChange={(e) => handleChange("status", e.target.value)}
            className="min-h-[44px] w-full min-w-0 rounded border bg-white p-2"
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>

          <select
            value={form.role || ""}
            onChange={(e) => handleChange("role", e.target.value)}
            className="min-h-[44px] w-full min-w-0 rounded border bg-white p-2"
          >
            <option value="">Select Role</option>
            <option value="Primary">Primary</option>
            <option value="Admin">Admin</option>
            <option value="Finance">Finance</option>
            <option value="Other">Other</option>
            <option value="Client">Client</option>
          </select>
        </div>

        <select
          value={form.access_level || ""}
          onChange={(e) => handleChange("access_level", e.target.value)}
          className="min-h-[44px] w-full min-w-0 rounded border bg-white p-2"
        >
          <option value="">Select Access Level</option>
          <option value="Organisation Manager">Organisation Manager</option>
          <option value="Client User Manager">Client User Manager</option>
          <option value="Job Viewer">Job Viewer</option>
          <option value="Portal Access Manager">Portal Access Manager</option>
          <option value="Brand Manager">Brand Manager</option>
        </select>

        {/* Flags */}
        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:gap-5">
            <label className="inline-flex min-h-[44px] items-center">
              <input
                type="checkbox"
                checked={!!form.portal_access}
                onChange={(e) => handleChange("portal_access", e.target.checked)}
                className="mr-2 h-5 w-5 flex-none"
              />
              Portal Access
            </label>
            <label className="inline-flex min-h-[44px] items-center">
              <input
                type="checkbox"
                checked={!!form.is_primary}
                onChange={(e) => handleChange("is_primary", e.target.checked)}
                className="mr-2 h-5 w-5 flex-none"
              />
              Primary Contact
            </label>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:gap-5">
            <label className="inline-flex min-h-[44px] items-center">
              <input
                type="checkbox"
                checked={!!form.is_billing_contact}
                onChange={(e) => handleChange("is_billing_contact", e.target.checked)}
                className="mr-2 h-5 w-5 flex-none"
              />
              Billing Contact
            </label>
            <label className="inline-flex min-h-[44px] items-center">
              <input
                type="checkbox"
                checked={!!form.is_technical_contact}
                onChange={(e) => handleChange("is_technical_contact", e.target.checked)}
                className="mr-2 h-5 w-5 flex-none"
              />
              Technical Contact
            </label>
          </div>
        </div>

        {/* Notes */}
        <textarea
          value={form.notes || ""}
          onChange={(e) => handleChange("notes", e.target.value)}
          placeholder="Notes"
          className="min-h-[96px] w-full resize-y rounded border p-2"
          rows={3}
        />
      </div>

      <div className="mt-5 grid grid-cols-1 sm:flex sm:justify-end">
        <button
          onClick={handleUpdate}
          className="min-h-[44px] w-full touch-manipulation rounded bg-btnActionNormal px-4 py-2 font-semibold text-white shadow transition-colors duration-200 hover:bg-btnActionHover active:bg-btnActionClick sm:w-auto"
        >
          Update Contact
        </button>
      </div>
    </ModalWrapper>
  );
};

export default EditContactModal;
